import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { Provider as ReduxProvider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import './assets/base.scss';
import configureAppStore from './config/configureStore';
import ErrorBoundary from './components/ErrorBoundary';
import { OrbitRoutes } from './orbit/routes';

import { AuthProvider } from './orbit/auth/AuthProvider';
import { BrandProvider } from './orbit/brand/BrandProvider';
import { MeProvider } from './orbit/me/MeProvider';
import { ApiClientProvider } from './orbit/api/client.jsx';
import { OperatorBootstrapProvider } from './orbit/bootstrap/OperatorBootstrapProvider';

/**
 * Orbit Bootstrap.
 *
 *  - BrowserRouter (NICHT HashRouter wie im Template-Default) — wir wollen
 *    teilbare Permalinks; SPA-Fallback liefert `server.js`.
 *  - Provider-Order: Redux (Theme) → Auth → Brand → ApiClient (refs)
 *    → Me → OperatorBootstrap (orchestriert /brands + /me) → QueryClient.
 */
const store = configureAppStore();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

const renderApp = () => (
  <React.StrictMode>
    <ErrorBoundary>
      <ReduxProvider store={store}>
        <BrowserRouter>
          <AuthProvider>
            <BrandProvider>
              <ApiClientProvider>
                <MeProvider>
                  <OperatorBootstrapProvider>
                    <QueryClientProvider client={queryClient}>
                      <OrbitRoutes />
                    </QueryClientProvider>
                  </OperatorBootstrapProvider>
                </MeProvider>
              </ApiClientProvider>
            </BrandProvider>
          </AuthProvider>
        </BrowserRouter>
      </ReduxProvider>
    </ErrorBoundary>
  </React.StrictMode>
);

const root = createRoot(document.getElementById('root'));
root.render(renderApp());

if (import.meta.hot) {
  import.meta.hot.accept();
}
